import { AlertTriangle, ArrowLeft, Atom, FlaskConical, RotateCcw, Sparkles, TestTube } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  ELEMENT_CLASS_LABELS,
  ELEMENT_LAB_SAMPLES,
  METALS_DATA_PROVENANCE,
  PROPERTY_TESTS,
  checkClassification,
  elementTestRecord,
  runPropertyTest,
  type ElementClass,
  type Evidence,
  type PropertyTestId,
  type TestReading,
} from './metalsNonmetals';
import { MetalsDensityTest } from './MetalsDensityTest';
import { CircuitTester, MeltingScale, PeriodicMap } from './MetalsNonmetalsVisuals';
import { StudyShell } from './StudyShell';
import { toolsHubPath } from './studyTools';

const CLASSES: ElementClass[] = ['metal', 'non-metal', 'metalloid'];
const LAB_SYMBOLS = ELEMENT_LAB_SAMPLES.map((sample) => sample.symbol);
const PERIODIC_TABLE_PATH = '/learn/study/subjects/chemistry/tools/periodic-table';

const EVIDENCE_LABELS: Record<Evidence, string> = {
  'metal-like': 'Points to metal',
  'non-metal-like': 'Points to non-metal',
  'in-between': 'In between',
  'not-decisive': 'Does not decide',
};

const EVIDENCE_TONES: Record<Evidence, string> = {
  'metal-like': 'bg-wash-sky text-ink',
  'non-metal-like': 'bg-wash-sunshine text-ink',
  'in-between': 'bg-wash-mint text-ink',
  'not-decisive': 'bg-surface text-slate2',
};

function ChoiceGroup({
  label,
  value,
  disabled,
  onChoose,
}: {
  label: string;
  value: ElementClass | null;
  disabled: boolean;
  onChoose: (value: ElementClass) => void;
}) {
  return (
    <div className="mt-3 grid gap-2 sm:grid-cols-3" role="group" aria-label={label}>
      {CLASSES.map((option) => (
        <button
          key={option}
          type="button"
          disabled={disabled}
          aria-pressed={value === option}
          onClick={() => onChoose(option)}
          className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold transition-colors disabled:cursor-default ${
            value === option ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-surface text-slate2 hover:border-brand-sky/40'
          } disabled:opacity-70`}
        >
          {ELEMENT_CLASS_LABELS[option]}
        </button>
      ))}
    </div>
  );
}

export function MetalsNonmetalsLabPage() {
  const [symbol, setSymbol] = useState(ELEMENT_LAB_SAMPLES[0].symbol);
  const [prediction, setPrediction] = useState<ElementClass | null>(null);
  const [testsRun, setTestsRun] = useState<PropertyTestId[]>([]);
  const [answer, setAnswer] = useState<ElementClass | null>(null);
  const [checked, setChecked] = useState(false);

  const record = elementTestRecord(symbol);
  const feedback = useMemo(
    () => (checked && answer ? checkClassification(symbol, answer, prediction) : null),
    [checked, answer, symbol, prediction],
  );

  const chooseElement = (next: string) => {
    setSymbol(next);
    setPrediction(null);
    setTestsRun([]);
    setAnswer(null);
    setChecked(false);
  };

  const runTest = (test: PropertyTestId) => {
    setTestsRun((current) => (current.includes(test) ? current : [...current, test]));
  };

  if (!record) {
    return (
      <StudyShell>
        <p role="alert" className="rounded-2xl bg-wash-coral px-4 py-3 font-bold text-ink">
          This element is not supported by the lab data.
        </p>
      </StudyShell>
    );
  }

  const { sample } = record;
  const allTestsRun = testsRun.length === PROPERTY_TESTS.length;
  const readings = PROPERTY_TESTS.filter((test) => testsRun.includes(test.id))
    .map((test) => runPropertyTest(symbol, test.id))
    .filter((reading): reading is TestReading => reading !== null);

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'metals-nonmetals', state: { symbol, prediction, testsRun, answer, answer_revealed: checked, ...(checked ? { visible_result: feedback } : {}) } }}>
      <Link
        to={toolsHubPath('chemistry')}
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-mint p-6 shadow-card-soft lg:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
              <Atom className="h-4 w-4" aria-hidden="true" /> Elements &amp; atoms
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Metals &amp; Non-metals Lab
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Predict, test, then classify by evidence. No single test decides — watch for the
              exceptions.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Explore tool · Year 8+
          </div>
        </div>
      </header>

      <section className="mt-6 grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
        <aside className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6">
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Choose an element</p>
          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5 xl:grid-cols-3" role="group" aria-label="Elements to test">
            {ELEMENT_LAB_SAMPLES.map((item) => (
              <button
                key={item.symbol}
                type="button"
                aria-pressed={item.symbol === symbol}
                aria-label={item.name}
                onClick={() => chooseElement(item.symbol)}
                className={`rounded-2xl border px-2 py-2 text-center transition-colors ${
                  item.symbol === symbol ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-surface text-slate2 hover:border-brand-sky/40'
                }`}
              >
                <span className="block text-lg font-extrabold">{item.symbol}</span>
                <span className="block truncate text-[11px] font-bold">{item.name.replace(' (graphite)', '')}</span>
              </button>
            ))}
          </div>
          <div className="mt-5 rounded-[1.5rem] border border-hairline bg-surface p-3">
            <PeriodicMap selected={symbol} labSymbols={LAB_SYMBOLS} showClasses={checked} />
          </div>
          <p className="mt-3 text-sm font-bold text-ink">
            {sample.name}: Group {record.position.group}, Period {record.position.period}
          </p>
          <Link to={PERIODIC_TABLE_PATH} className="mt-1 inline-block text-sm font-extrabold text-brand-sky hover:text-ink">
            Open the Periodic Table tool
          </Link>
        </aside>

        <div className="min-w-0 space-y-6">
          <article className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">1 · Predict</p>
            <h2 className="mt-2 text-2xl font-extrabold text-ink">{sample.name}</h2>
            <p className="mt-1 text-sm font-medium text-slate2">Sample: {sample.sample}</p>
            <p className="mt-3 font-medium leading-7 text-slate2">Before testing, what do you think it is?</p>
            <ChoiceGroup label="Your prediction" value={prediction} disabled={testsRun.length > 0} onChoose={setPrediction} />
          </article>

          <article className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">2 · Test</p>
                <h2 className="mt-2 text-xl font-extrabold text-ink">Run the property tests</h2>
              </div>
              <button
                type="button"
                disabled={!prediction || allTestsRun}
                onClick={() => setTestsRun(PROPERTY_TESTS.map((test) => test.id))}
                className="inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink hover:bg-surface disabled:opacity-40"
              >
                <FlaskConical className="h-4 w-4" aria-hidden="true" /> Run all tests
              </button>
            </div>
            {!prediction ? (
              <p className="mt-3 text-sm font-bold text-slate2">Make a prediction first to unlock the tests.</p>
            ) : null}
            <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {PROPERTY_TESTS.map((test) => (
                <button
                  key={test.id}
                  type="button"
                  disabled={!prediction || testsRun.includes(test.id)}
                  onClick={() => runTest(test.id)}
                  className="flex items-start gap-2 rounded-2xl border border-hairline bg-surface px-3 py-2 text-left hover:border-brand-sky/50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <TestTube className="mt-0.5 h-4 w-4 shrink-0 text-brand-sky" aria-hidden="true" />
                  <span>
                    <span className="block text-sm font-extrabold text-ink">{test.label}</span>
                    <span className="block text-xs font-medium leading-5 text-slate2">{test.question}</span>
                  </span>
                </button>
              ))}
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <div className="rounded-[1.5rem] border border-hairline bg-surface p-3">
                <CircuitTester result={testsRun.includes('electrical') ? sample.electrical : null} sample={sample.sample} />
              </div>
              <div className="rounded-[1.5rem] border border-hairline bg-surface p-3">
                {testsRun.includes('melting') ? (
                  <MeltingScale name={sample.name} meltingPointC={record.meltingPointC} />
                ) : (
                  <p className="flex h-full min-h-28 items-center justify-center text-center text-xs font-extrabold text-slate2">
                    Run the melting point test to place it on the scale
                  </p>
                )}
              </div>
            </div>

            {readings.length > 0 ? (
              <ul className="mt-5 grid gap-3 md:grid-cols-2" aria-label="Test results">
                {readings.map((reading) => (
                  <li key={reading.test} className="rounded-2xl border border-hairline bg-white p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-extrabold text-ink">
                        {PROPERTY_TESTS.find((test) => test.id === reading.test)?.label}: {reading.result}
                      </p>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-extrabold ${EVIDENCE_TONES[reading.evidence]}`}>
                        {EVIDENCE_LABELS[reading.evidence]}
                      </span>
                    </div>
                    <p className="mt-1 text-xs font-medium leading-5 text-slate2">{reading.observation}</p>
                  </li>
                ))}
              </ul>
            ) : null}
          </article>

          <article className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8">
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-mint">3 · Classify by evidence</p>
            <h2 className="mt-2 text-xl font-extrabold text-ink">What does the evidence say?</h2>
            {!allTestsRun ? (
              <p className="mt-2 text-sm font-bold text-slate2">
                Run all six tests first ({testsRun.length}/{PROPERTY_TESTS.length} done).
              </p>
            ) : null}
            <ChoiceGroup label="Classify by evidence" value={answer} disabled={!allTestsRun || checked} onChoose={setAnswer} />
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                disabled={!answer || checked || !allTestsRun}
                onClick={() => setChecked(true)}
                className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Sparkles className="h-4 w-4" aria-hidden="true" /> Check my classification
              </button>
              <button
                type="button"
                onClick={() => chooseElement(symbol)}
                className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink hover:bg-surface"
              >
                <RotateCcw className="h-4 w-4" aria-hidden="true" /> Start again
              </button>
            </div>

            {feedback ? (
              <div
                role="status"
                className={`mt-5 rounded-[1.5rem] border p-5 ${feedback.correct ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/35 bg-wash-coral'}`}
              >
                <p className="font-extrabold text-ink">{feedback.headline}</p>
                <p className="mt-2 font-medium leading-7 text-slate2">{feedback.explanation}</p>
                {feedback.predictionNote ? (
                  <p className="mt-2 text-sm font-bold leading-6 text-ink-soft">{feedback.predictionNote}</p>
                ) : null}
                {feedback.exception ? (
                  <div className="mt-4 flex items-start gap-3 rounded-2xl border border-brand-sunshine bg-wash-sunshine p-4">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-ink" aria-hidden="true" />
                    <div>
                      <p className="text-sm font-extrabold text-ink">Exception: {feedback.exception.title}</p>
                      <p className="mt-1 text-sm font-medium leading-6 text-ink-soft">{feedback.exception.note}</p>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </article>
        </div>
      </section>

      <MetalsDensityTest />

      <section className="mt-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6" aria-labelledby="metals-sources">
        <h2 id="metals-sources" className="text-lg font-extrabold text-ink">Data and conditions</h2>
        <p className="mt-2 text-sm font-medium leading-6 text-slate2">{METALS_DATA_PROVENANCE.conditions}</p>
        <details className="mt-3 text-xs font-medium leading-5 text-slate2">
          <summary className="cursor-pointer font-extrabold text-ink">Sources</summary>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {METALS_DATA_PROVENANCE.sources.map((source) => (
              <li key={source}>{source}</li>
            ))}
          </ul>
          <p className="mt-2">Results are awaiting teaching review.</p>
        </details>
      </section>

      <footer className="mt-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-sm font-medium leading-6 text-slate2">
        Model boundary: results are reviewed descriptions of pure samples, ranked as good, moderate or
        poor — not measurements. Real samples can carry oxide layers or impurities that change what
        you see. Metalloid membership varies slightly between textbooks. Mercury, bromine and iodine
        are shown as data only: they are toxic and are not handled in school practical work. Practical
        testing needs teacher supervision and a risk assessment. Nothing on this page is saved or scored.
      </footer>
    </StudyShell>
  );
}
